import { ILiveTokenResponse } from "../store/@types/ai";

const LIVE_WS =
  "wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContentConstrained";

const CAPTURE_WORKLET = "/audio-processors/capture.worklet.js";
const PLAYBACK_WORKLET = "/audio-processors/playback.worklet.js";

type AudioContextCtor = typeof AudioContext;

const AudioContextClass: AudioContextCtor =
  window.AudioContext ||
  (window as Window & { webkitAudioContext?: AudioContextCtor })
    .webkitAudioContext!;

const pcm16ToBase64 = (float32Array: Float32Array): string => {
  const int16Array = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    int16Array[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  const bytes = new Uint8Array(int16Array.buffer);
  let binary = "";
  for (let j = 0; j < bytes.length; j += 1) {
    binary += String.fromCharCode(bytes[j]);
  }
  return window.btoa(binary);
};

const decodeBase64Pcm = (base64Audio: string): Float32Array => {
  const binaryString = atob(base64Audio);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const inputArray = new Int16Array(bytes.buffer);
  const float32Data = new Float32Array(inputArray.length);
  for (let j = 0; j < inputArray.length; j += 1) {
    float32Data[j] = inputArray[j] / 32768;
  }
  return float32Data;
};

class AudioStreamer {
  private audioContext: AudioContext | null = null;
  private audioWorklet: AudioWorkletNode | null = null;
  private mediaStream: MediaStream | null = null;
  private audioSource: MediaStreamAudioSourceNode | null = null;
  private isStreaming = false;

  constructor(
    private readonly workletUrl: string,
    private readonly onAudioChunk: (data: Float32Array) => void,
  ) {}

  async start(): Promise<void> {
    if (this.isStreaming) return;

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    if (!this.audioContext) {
      this.audioContext = new AudioContextClass({ sampleRate: 16000 });
      await this.audioContext.audioWorklet.addModule(this.workletUrl);
      this.audioWorklet = new AudioWorkletNode(
        this.audioContext,
        "audio-capture-processor",
      );
      this.audioWorklet.port.onmessage = (event: MessageEvent) => {
        if (!this.isStreaming || !event.data || event.data.type !== "audio") {
          return;
        }
        this.onAudioChunk(event.data.data);
      };
    }

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.audioSource = this.audioContext.createMediaStreamSource(
      this.mediaStream,
    );
    if (this.audioWorklet) {
      this.audioSource.connect(this.audioWorklet);
    }
    this.isStreaming = true;
  }

  stop(): void {
    this.isStreaming = false;
    this.audioSource?.disconnect();
    this.audioSource = null;
    if (this.audioContext?.state === "running") {
      void this.audioContext.suspend();
    }
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
  }

  destroy(): void {
    this.stop();
    void this.audioContext?.close();
    this.audioContext = null;
  }
}

class AudioPlayer {
  private audioContext: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private gainNode: GainNode | null = null;
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;

  constructor(private readonly workletUrl: string) {}

  async init(): Promise<void> {
    if (this.isInitialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      this.audioContext = new AudioContextClass({ sampleRate: 24000 });
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      await this.audioContext.audioWorklet.addModule(this.workletUrl);
      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        "pcm-playback-processor",
      );
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = 1;
      this.workletNode.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      this.isInitialized = true;
    })();

    return this.initPromise;
  }

  async play(base64Audio: string): Promise<void> {
    await this.init();
    if (this.audioContext?.state === "suspended") {
      await this.audioContext.resume();
    }
    this.workletNode?.port.postMessage(decodeBase64Pcm(base64Audio));
  }

  interrupt(): void {
    this.workletNode?.port.postMessage("interrupt");
  }

  destroy(): void {
    void this.audioContext?.close();
    this.audioContext = null;
    this.workletNode = null;
    this.gainNode = null;
    this.isInitialized = false;
    this.initPromise = null;
  }
}

export type LiveCallStatus =
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking";

export type LiveCallHandlers = {
  onStatus: (status: LiveCallStatus) => void;
  onUnexpectedEnd: () => void;
};

export class GeminiLiveCall {
  private socket: WebSocket | null = null;
  private streamer: AudioStreamer | null = null;
  private player: AudioPlayer | null = null;
  private endedByUser = false;
  private speaking = false;
  private connected = false;
  private turnTimer: number | null = null;
  private handlers: LiveCallHandlers | null = null;

  get isActive(): boolean {
    return this.connected;
  }

  async start(
    session: ILiveTokenResponse,
    handlers: LiveCallHandlers,
  ): Promise<void> {
    this.stop(false);
    this.endedByUser = false;
    this.handlers = handlers;
    this.player = new AudioPlayer(PLAYBACK_WORKLET);
    await this.player.init();
    await this.connectSocket(session);
    this.streamer = new AudioStreamer(CAPTURE_WORKLET, (chunk) => {
      this.sendAudioChunk(chunk);
    });
    await this.streamer.start();
    this.connected = true;
    handlers.onStatus("listening");
  }

  interrupt(): void {
    if (!this.speaking) return;
    this.speaking = false;
    this.player?.interrupt();
    this.handlers?.onStatus("listening");
  }

  stop(announceUnexpected = false): void {
    this.endedByUser = true;
    this.speaking = false;
    this.connected = false;
    this.clearTurnTimer();
    this.streamer?.destroy();
    this.streamer = null;
    this.player?.interrupt();
    this.player?.destroy();
    this.player = null;
    if (this.socket) {
      try {
        if (this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(
            JSON.stringify({ realtimeInput: { audioStreamEnd: true } }),
          );
        }
      } catch {
        /* ignore */
      }
      try {
        this.socket.close();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
    if (announceUnexpected) {
      this.handlers?.onUnexpectedEnd();
    }
    this.handlers = null;
  }

  private sendAudioChunk(float32Audio: Float32Array): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(
      JSON.stringify({
        realtimeInput: {
          audio: {
            mimeType: "audio/pcm;rate=16000",
            data: pcm16ToBase64(float32Audio),
          },
        },
      }),
    );
  }

  private connectSocket(session: ILiveTokenResponse): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const url = `${LIVE_WS}?access_token=${encodeURIComponent(session.token || "")}`;
      this.socket = new WebSocket(url);

      const resolveOnce = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const rejectOnce = (error: Error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      this.socket.onopen = () => {
        const setup = session.setup || {
          model: "models/gemini-3.1-flash-live-preview",
        };
        this.socket?.send(JSON.stringify({ setup }));
      };
      this.socket.onerror = () => {
        rejectOnce(new Error("Connection error while starting the live call."));
      };
      this.socket.onclose = (event: CloseEvent) => {
        if (!settled) {
          const closeDetails = [
            `WebSocket ${event.code}`,
            event.reason,
          ]
            .filter(Boolean)
            .join(": ");
          rejectOnce(
            new Error(
              `The live call closed before setup completed (${closeDetails}).`,
            ),
          );
          return;
        }
        if (!this.endedByUser && this.connected) {
          this.connected = false;
          this.handlers?.onUnexpectedEnd();
        }
      };
      this.socket.onmessage = async (event) => {
        let raw: string;
        if (event.data instanceof Blob) {
          raw = await event.data.text();
        } else if (event.data instanceof ArrayBuffer) {
          raw = new TextDecoder().decode(event.data);
        } else {
          raw = String(event.data);
        }
        try {
          const payload = JSON.parse(raw) as Record<string, unknown>;
          this.handlePayload(payload, resolveOnce, rejectOnce);
        } catch {
          /* ignore malformed payload */
        }
      };
    });
  }

  private handlePayload(
    payload: Record<string, unknown>,
    setupResolve: () => void,
    setupReject: (error: Error) => void,
  ): void {
    if (payload.setupComplete) {
      setupResolve();
      return;
    }
    const error = payload.error as { message?: string } | undefined;
    if (error) {
      setupReject(new Error(error.message || "Live call setup failed."));
      return;
    }

    const serverContent = (payload.serverContent || {}) as Record<
      string,
      unknown
    >;
    if (serverContent.interrupted) {
      this.speaking = false;
      this.player?.interrupt();
      this.handlers?.onStatus("listening");
    }

    const inputTranscription = serverContent.inputTranscription as
      | { text?: string; finished?: boolean }
      | undefined;
    if (
      inputTranscription &&
      typeof inputTranscription.text === "string" &&
      inputTranscription.text.trim() !== "" &&
      !this.speaking
    ) {
      this.handlers?.onStatus(
        inputTranscription.finished ? "thinking" : "listening",
      );
    }

    const modelTurn = (serverContent.modelTurn || {}) as {
      parts?: Array<{ inlineData?: { data?: string } }>;
    };
    const parts = Array.isArray(modelTurn.parts) ? modelTurn.parts : [];
    parts.forEach((part) => {
      if (!part?.inlineData?.data) return;
      this.speaking = true;
      this.clearTurnTimer();
      this.handlers?.onStatus("speaking");
      void this.player?.play(part.inlineData.data);
    });

    if (serverContent.turnComplete) {
      this.clearTurnTimer();
      this.turnTimer = window.setTimeout(() => {
        this.speaking = false;
        this.handlers?.onStatus("listening");
      }, 250);
    }
  }

  private clearTurnTimer(): void {
    if (this.turnTimer) {
      window.clearTimeout(this.turnTimer);
      this.turnTimer = null;
    }
  }
}

export const isSecureAudioContext = (): boolean =>
  window.isSecureContext ||
  window.location.protocol === "https:" ||
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";
